/** #434 (SPEC-434 3.1): one grid. Versions with Open / Compare / Download / Activate / Retire / Delete, and Used by. */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { PageHeader } from '@/components/layout';
import { Badge, Button, Card, LoadingSpinner, Modal, ModalFooter } from '@/components/common';
import {
  commissionGridService,
  type GridPublishResult,
  type GridRecalcBatch,
  type GridRetirePreview,
  type GridVersionDetail,
} from '@/services/commissionGrids';
import { getErrorMessage } from '@/services/api';
import { useAuthStore } from '@/store/auth';
import { RecalcPreview } from '../GridImportWizardPage';
import {
  STATUS_LABELS,
  canWriteGrids,
  defaultComparePair,
  saveBlob,
  statusVariant,
  versionActions,
} from './gridPages';

type Tab = 'versions' | 'usage';

export function GridPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const client = useQueryClient();
  const role = useAuthStore((s) => s.user?.role);
  const canWrite = canWriteGrids(role);
  const [tab, setTab] = useState<Tab>('versions');
  const [activating, setActivating] = useState<{
    version: GridVersionDetail;
    batch: GridRecalcBatch | null;
  } | null>(null);
  const [retiring, setRetiring] = useState<{
    version: GridVersionDetail;
    reason: string;
    preview: GridRetirePreview | null;
  } | null>(null);
  const [busy, setBusy] = useState(false);

  const grid = useQuery({
    queryKey: ['commission-grid', id],
    queryFn: () => commissionGridService.getGrid(id),
  });
  const usage = useQuery({
    queryKey: ['commission-grid-usage', id],
    queryFn: () => commissionGridService.usage(id),
    enabled: tab === 'usage',
  });
  const refresh = () => client.invalidateQueries({ queryKey: ['commission-grid', id] });

  const discard = useMutation({
    mutationFn: (v: GridVersionDetail) => commissionGridService.discard(v.id),
    onSuccess: () => {
      toast.success('Draft deleted.');
      void refresh();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const startActivate = async (version: GridVersionDetail) => {
    setBusy(true);
    try {
      const batch = await commissionGridService.recalcPreview(version.id);
      setActivating({ version, batch });
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const confirmActivate = async () => {
    if (!activating?.batch) return;
    setBusy(true);
    try {
      const done: GridPublishResult = await commissionGridService.publish(
        activating.version.id,
        activating.batch.id
      );
      toast.success(`Version ${done.version_no} is live.`);
      setActivating(null);
      void refresh();
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const previewRetire = async () => {
    if (!retiring || !retiring.reason.trim()) return;
    setBusy(true);
    try {
      const preview = await commissionGridService.retirePreview(
        retiring.version.id,
        retiring.reason.trim()
      );
      setRetiring({ ...retiring, preview });
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const confirmRetire = async () => {
    if (!retiring) return;
    setBusy(true);
    try {
      await commissionGridService.retire(retiring.version.id, retiring.reason.trim());
      toast.success('Version withdrawn.');
      setRetiring(null);
      void refresh();
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const download = async (v: GridVersionDetail) => {
    try {
      saveBlob(await commissionGridService.downloadOriginal(v.id), v.file_name || 'grid.xlsx');
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  if (grid.isLoading) return <LoadingSpinner />;
  if (grid.error || !grid.data) {
    return (
      <p role="alert" className="text-sm text-red-600">
        {grid.error ? getErrorMessage(grid.error) : 'Grid not found.'}
      </p>
    );
  }
  const data = grid.data;
  const pair = defaultComparePair(data.versions);

  return (
    <div>
      <PageHeader
        title={data.name}
        subtitle={`${data.insurer_name} - ${data.broker_name}`}
        breadcrumbs={[
          { label: 'Commissions', href: '/commissions' },
          { label: 'Grids', href: '/commissions/grids' },
          { label: data.name },
        ]}
        actions={
          pair ? (
            <Button
              variant="outline"
              onClick={() =>
                navigate(`/commissions/grids/${id}/compare?from=${pair.from}&to=${pair.to}`)
              }
            >
              Compare latest two
            </Button>
          ) : undefined
        }
      />
      <div role="tablist" className="mb-4 flex gap-2">
        {(['versions', 'usage'] as Tab[]).map((t) => (
          <Button
            key={t}
            role="tab"
            aria-selected={tab === t}
            variant={tab === t ? 'primary' : 'outline'}
            size="sm"
            onClick={() => setTab(t)}
          >
            {t === 'versions' ? 'Versions' : 'Used by'}
          </Button>
        ))}
      </div>

      {tab === 'versions' && (
        <Card>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 dark:text-gray-400">
                  <th className="py-2 pr-4">Version</th>
                  <th className="py-2 pr-4">Window</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4">Rows</th>
                  <th className="py-2 pr-4">File</th>
                  <th className="py-2 pr-4">Uploaded</th>
                  <th className="py-2 pr-4">Activated / withdrawn</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {data.versions.map((v) => {
                  const actions = versionActions(v, canWrite);
                  const others = data.versions.filter(
                    (o) => o.id !== v.id && o.version_no < v.version_no
                  );
                  return (
                    <tr
                      key={v.id}
                      className="border-t border-gray-100 align-top dark:border-gray-700"
                    >
                      <td className="py-2 pr-4 font-medium">v{v.version_no}</td>
                      <td className="py-2 pr-4">
                        {v.effective_from} to {v.effective_until ?? 'open'}
                      </td>
                      <td className="py-2 pr-4">
                        <Badge variant={statusVariant(v.status)}>
                          {STATUS_LABELS[v.status] ?? v.status}
                        </Badge>
                      </td>
                      <td className="py-2 pr-4">{v.row_count ?? '-'}</td>
                      <td className="py-2 pr-4">{v.file_name}</td>
                      <td className="py-2 pr-4">
                        {v.uploaded_by ?? '-'}
                        <div className="text-xs text-gray-500">{v.uploaded_at?.slice(0, 10)}</div>
                      </td>
                      <td className="py-2 pr-4">
                        {v.status === 'withdrawn' ? (
                          <span>
                            {v.withdrawn_by ?? '-'} {v.withdrawn_at?.slice(0, 10)}
                            {v.withdrawn_reason && (
                              <div className="text-xs text-gray-500">{v.withdrawn_reason}</div>
                            )}
                          </span>
                        ) : (
                          <span>
                            {v.activated_by ?? '-'} {v.activated_at?.slice(0, 10)}
                          </span>
                        )}
                      </td>
                      <td className="space-x-2 whitespace-nowrap py-2 text-right">
                        <Link
                          className="text-primary-600 hover:underline"
                          to={`/commissions/grid-versions/${v.id}`}
                        >
                          Open
                        </Link>
                        {others[0] && (
                          <Link
                            className="text-primary-600 hover:underline"
                            to={`/commissions/grids/${id}/compare?from=${others[0].id}&to=${v.id}`}
                          >
                            Compare
                          </Link>
                        )}
                        {canWrite && v.has_file && (
                          <Button size="sm" variant="ghost" onClick={() => download(v)}>
                            Download
                          </Button>
                        )}
                        {actions.activate && (
                          <Button size="sm" onClick={() => startActivate(v)} isLoading={busy}>
                            Activate
                          </Button>
                        )}
                        {actions.retire && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setRetiring({ version: v, reason: '', preview: null })}
                          >
                            Retire
                          </Button>
                        )}
                        {actions.discard && (
                          <Button size="sm" variant="ghost" onClick={() => discard.mutate(v)}>
                            Delete draft
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {tab === 'usage' && (
        <Card>
          {usage.isLoading && <LoadingSpinner />}
          {usage.error && (
            <p role="alert" className="text-sm text-red-600">
              {getErrorMessage(usage.error)}
            </p>
          )}
          {usage.data && (
            <div className="space-y-3 text-sm">
              <p>
                Held by <strong>{usage.data.broker_name}</strong>. Classes covered:{' '}
                {usage.data.classes.join(', ') || '-'}.
              </p>
              <p className="text-gray-500">
                Policies priced by each version in the last 12 months.
              </p>
              <ul className="space-y-1">
                {usage.data.versions.map((v) => (
                  <li key={v.version_id}>
                    v{v.version_no} ({STATUS_LABELS[v.status] ?? v.status}): {v.policies} policies,
                    premium {v.premium}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Card>
      )}

      <Modal
        isOpen={activating !== null}
        onClose={() => setActivating(null)}
        title="Activate this version"
        size="xl"
      >
        {activating?.batch && <RecalcPreview batch={activating.batch} />}
        <ModalFooter>
          <Button variant="outline" onClick={() => setActivating(null)}>
            Cancel
          </Button>
          <Button onClick={confirmActivate} isLoading={busy}>
            Confirm and go live
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={retiring !== null}
        onClose={() => setRetiring(null)}
        title="Retire this version"
      >
        {retiring && (
          <div className="space-y-3 text-sm">
            <label className="block">
              <span className="mb-1 block font-medium">Reason (required)</span>
              <textarea
                className="w-full rounded-md border border-gray-300 p-2 dark:border-gray-600 dark:bg-gray-800"
                rows={3}
                value={retiring.reason}
                onChange={(e) =>
                  setRetiring({ ...retiring, reason: e.target.value, preview: null })
                }
              />
            </label>
            {retiring.preview && (
              <div
                role="alert"
                className="rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-900"
              >
                <p className="font-semibold">
                  {retiring.preview.policies} policies were priced by this version (commission{' '}
                  {retiring.preview.commission}).
                </p>
                <p>{retiring.preview.message}</p>
              </div>
            )}
          </div>
        )}
        <ModalFooter>
          <Button variant="outline" onClick={() => setRetiring(null)}>
            Cancel
          </Button>
          {retiring?.preview ? (
            <Button onClick={confirmRetire} isLoading={busy}>
              Confirm withdrawal
            </Button>
          ) : (
            <Button onClick={previewRetire} isLoading={busy} disabled={!retiring?.reason.trim()}>
              Review impact
            </Button>
          )}
        </ModalFooter>
      </Modal>
    </div>
  );
}
